import { Component, inject, NgZone, OnDestroy, OnInit, Renderer2, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationCancel, NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import { filter, Subscription, take } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { AppFooter } from './app.footer';
import { LayoutService } from '../service/layout.service';
import { IdleTimeoutService } from 'src/services/idle-timeout.service';
import { DriverPositionService } from 'src/services/driver-position.service';
import { MessageModule } from 'primeng/message';
import { ButtonModule } from 'primeng/button';
import { BRAND_LOADER_SESSION_KEY, BrandLoaderComponent } from '@/components/brand-loader/brand-loader.component';

@Component({
    selector: 'app-layout',
    standalone: true,
    imports: [CommonModule, AppTopbar, AppSidebar, RouterModule, AppFooter, MessageModule, ButtonModule, BrandLoaderComponent],
    template: `<div class="layout-wrapper" [ngClass]="containerClass" [inert]="brandLoaderVisible()">
        <app-topbar></app-topbar>
        <app-sidebar></app-sidebar>
        <div class="layout-main-container">
            <div class="layout-main">
                @if (driverPositionService.locationDenied()) {
                    <p-message severity="warn" styleClass="mb-4">
                        <div class="flex flex-col gap-2">
                            <strong>Sua localização está bloqueada</strong>
                            <span>Para acompanharmos a entrega, libere a localização:</span>
                            <ol class="list-decimal pl-5 m-0">
                                <li>Ligue a <b>Localização</b> do celular (menu de atalhos no topo da tela).</li>
                                <li>No Chrome, toque no ícone à esquerda do endereço do site &rarr; <b>Permissões</b> &rarr; <b>Localização</b> &rarr; <b>Permitir</b>.</li>
                                <li>Se não aparecer a opção: <b>Configurações do celular &rarr; Apps &rarr; Chrome &rarr; Permissões &rarr; Localização &rarr; Permitir durante o uso do app</b>.</li>
                            </ol>
                            <div>
                                <p-button label="Tentar novamente" icon="pi pi-refresh" size="small" (onClick)="driverPositionService.retry()" />
                            </div>
                        </div>
                    </p-message>
                }
                <router-outlet></router-outlet>
            </div>
            <app-footer></app-footer>
        </div>
        <div class="layout-mask animate-fadein"></div>
    </div>
    @if (brandLoaderVisible()) {
        <app-brand-loader label="Carregando sistema" status="Preparando o sistema"
            [progress]="brandLoaderProgress()" [leaving]="brandLoaderLeaving()" />
    }`
})
export class AppLayout implements OnInit, OnDestroy {
    overlayMenuOpenSubscription: Subscription;

    menuOutsideClickListener: any;

    @ViewChild(AppSidebar) appSidebar!: AppSidebar;

    @ViewChild(AppTopbar) appTopBar!: AppTopbar;

    // Loader de entrada para perfis sem dashboard (o dashboard tem o seu, com progresso por gráfico).
    // Fica só enquanto a primeira tela do perfil carrega, entre um mínimo e um teto.
    private readonly zone = inject(NgZone);
    private readonly brandLoaderMinMs = 500;
    private readonly brandLoaderMaxMs = 1500;
    private brandLoaderFrame = 0;
    private brandLoaderTimers: ReturnType<typeof setTimeout>[] = [];

    brandLoaderVisible = signal(false);
    brandLoaderLeaving = signal(false);
    brandLoaderProgress = signal(0);

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private readonly idleTimeoutService: IdleTimeoutService,
        public readonly driverPositionService: DriverPositionService
    ) {
        this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
            if (!this.menuOutsideClickListener) {
                this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
                    if (this.isOutsideClicked(event)) {
                        this.hideMenu();
                    }
                });
            }

            if (this.layoutService.layoutState().staticMenuMobileActive) {
                this.blockBodyScroll();
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.hideMenu();
        });
    }

    ngOnInit() {
        this.idleTimeoutService.start();
        this.driverPositionService.start();
        this.startBrandLoader();
    }

    private startBrandLoader() {
        let pending = false;
        try {
            pending = sessionStorage.getItem(BRAND_LOADER_SESSION_KEY) === '1';
            sessionStorage.removeItem(BRAND_LOADER_SESSION_KEY);
        } catch { }

        if (!pending) return;

        this.brandLoaderVisible.set(true);
        const start = performance.now();
        let readyAt = 0;

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError),
            take(1)
        ).subscribe(() => {
            // Espera um quadro para a tela de destino estar desenhada.
            requestAnimationFrame(() => readyAt = performance.now());
        });

        let progress = 0;
        let last = start;

        const tick = (time: number) => {
            const elapsed = time - start;
            const delta = time - last;
            last = time;

            const done = elapsed >= this.brandLoaderMaxMs || (readyAt > 0 && elapsed >= this.brandLoaderMinMs);
            // Enquanto espera, aproxima-se de 90% sem chegar; pronto, completa rápido.
            const target = done ? 100 : 90;
            progress += (target - progress) * Math.min(1, delta / (done ? 90 : 350));
            if (done && 100 - progress < 0.5) progress = 100;

            this.brandLoaderProgress.set(progress);

            if (progress < 100) {
                this.brandLoaderFrame = requestAnimationFrame(tick);
                return;
            }

            this.brandLoaderTimers.push(setTimeout(() => {
                this.brandLoaderLeaving.set(true);
                this.brandLoaderTimers.push(setTimeout(() => this.brandLoaderVisible.set(false), 260));
            }, 120));
        };

        this.zone.runOutsideAngular(() => {
            this.brandLoaderFrame = requestAnimationFrame(tick);
        });
    }

    isOutsideClicked(event: MouseEvent) {
        const sidebarEl = document.querySelector('.layout-sidebar');
        const topbarEl = document.querySelector('.layout-menu-button');
        const eventTarget = event.target as Node;

        return !(sidebarEl?.isSameNode(eventTarget) || sidebarEl?.contains(eventTarget) || topbarEl?.isSameNode(eventTarget) || topbarEl?.contains(eventTarget));
    }

    hideMenu() {
        this.layoutService.layoutState.update((prev) => ({ ...prev, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
        }
    }

    get containerClass() {
        return {
            'layout-overlay': this.layoutService.layoutConfig().menuMode === 'overlay',
            'layout-static': this.layoutService.layoutConfig().menuMode === 'static',
            'layout-static-inactive': this.layoutService.layoutState().staticMenuDesktopInactive && this.layoutService.layoutConfig().menuMode === 'static',
            'layout-overlay-active': this.layoutService.layoutState().overlayMenuActive,
            'layout-mobile-active': this.layoutService.layoutState().staticMenuMobileActive
        };
    }

    ngOnDestroy() {
        this.idleTimeoutService.stop();
        this.driverPositionService.stop();
        cancelAnimationFrame(this.brandLoaderFrame);
        this.brandLoaderTimers.forEach(clearTimeout);

        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }
}
